import type { ParseResult } from 'papaparse'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Check, FileUp, FileWarning } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useRef, useState } from 'react'
import Papa from 'papaparse'
import { Button } from '@/components/ui/button'
import { Badge, Card } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { useImportClients, type ImportResult } from '@/features/clients/api'
import { ApiClientError } from '@/lib/api'

export const Route = createFileRoute('/_app/clients/import')({
  component: ImportPage,
})

const IMPORT_FIELDS = [
  { value: 'fullName', label: 'Full Name *' },
  { value: 'phone', label: 'Phone *' },
  { value: 'nic', label: 'NIC' },
  { value: 'gender', label: 'Gender' },
  { value: 'dob', label: 'Date of Birth (YYYY-MM-DD)' },
  { value: 'maritalStatus', label: 'Marital Status' },
  { value: 'nationality', label: 'Nationality' },
  { value: 'religion', label: 'Religion' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'address', label: 'Address' },
  { value: 'city', label: 'City' },
  { value: 'district', label: 'District' },
  { value: 'passportStatus', label: 'Passport Status' },
  { value: 'passportNo', label: 'Passport Number' },
  { value: 'passportExpiry', label: 'Passport Expiry (YYYY-MM-DD)' },
  { value: 'educationLevel', label: 'Education Level' },
  { value: 'experienceYears', label: 'Experience (Years)' },
  { value: 'expectedSalary', label: 'Expected Salary' },
  { value: 'salaryCurrency', label: 'Salary Currency' },
  { value: 'availableFrom', label: 'Available From (YYYY-MM-DD)' },
  { value: 'heightCm', label: 'Height (cm)' },
  { value: 'weightKg', label: 'Weight (kg)' },
  { value: 'emergencyName', label: 'Emergency Contact Name' },
  { value: 'emergencyPhone', label: 'Emergency Contact Phone' },
  { value: 'initialsName', label: 'Initials / Short Name' },
  { value: 'consentSms', label: 'Consent SMS (Yes/No)' },
  { value: 'consentWhatsapp', label: 'Consent WhatsApp (Yes/No)' },
  { value: 'consentEmail', label: 'Consent Email (Yes/No)' },
  { value: 'preferredCountries', label: 'Preferred Countries (; separated codes)' },
  { value: 'jobCategoryIds', label: 'Job Categories (; separated slugs)' },
] as const

const HEADER_MAP: Record<string, string> = {}
for (const f of IMPORT_FIELDS) {
  HEADER_MAP[f.value.toLowerCase().replace(/[\s_-]/g, '')] = f.value
  HEADER_MAP[f.label.toLowerCase().replace(/[\s_-]/g, '')] = f.value
}
HEADER_MAP['name'] = 'fullName'
HEADER_MAP['fullname'] = 'fullName'
HEADER_MAP['country'] = 'preferredCountries'
HEADER_MAP['countries'] = 'preferredCountries'
HEADER_MAP['jobcategory'] = 'jobCategoryIds'
HEADER_MAP['jobcategoryids'] = 'jobCategoryIds'
HEADER_MAP['category'] = 'jobCategoryIds'
HEADER_MAP['height'] = 'heightCm'
HEADER_MAP['weight'] = 'weightKg'
HEADER_MAP['salary'] = 'expectedSalary'
HEADER_MAP['currency'] = 'salaryCurrency'
HEADER_MAP['available'] = 'availableFrom'
HEADER_MAP['experience'] = 'experienceYears'
HEADER_MAP['emergencycontact'] = 'emergencyName'
HEADER_MAP['emergencyphone'] = 'emergencyPhone'
HEADER_MAP['emergency_name'] = 'emergencyName'
HEADER_MAP['emergency_phone'] = 'emergencyPhone'
HEADER_MAP['smsconsent'] = 'consentSms'
HEADER_MAP['whatsappconsent'] = 'consentWhatsapp'
HEADER_MAP['emailconsent'] = 'consentEmail'

function autoMapHeader(header: string): string {
  const normalised = header
    .toLowerCase()
    .replace(/[\s_-]/g, '')
    .replace(/[^a-z0-9]/g, '')
  return HEADER_MAP[normalised] ?? ''
}

function parseMappedValue(field: string, value: string): unknown {
  if (value === '' || value === undefined) return undefined

  if (field === 'consentSms' || field === 'consentWhatsapp' || field === 'consentEmail') {
    const lower = value.trim().toLowerCase()
    if (lower === 'yes' || lower === 'y' || lower === 'true' || lower === '1') return true
    return false
  }

  if (field === 'preferredCountries' || field === 'jobCategoryIds') {
    return value
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean)
  }

  if (
    field === 'experienceYears' ||
    field === 'heightCm' ||
    field === 'weightKg' ||
    field === 'expectedSalary'
  ) {
    const num = Number(value)
    return Number.isNaN(num) ? undefined : num
  }

  return value
}

function ImportPage() {
  const [step, setStep] = useState(0)
  const [fileName, setFileName] = useState('')
  const [headers, setHeaders] = useState<string[]>([])
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [duplicatePolicy, setDuplicatePolicy] = useState<'skip' | 'error'>('skip')
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const importMut = useImportClients()

  const requiredMapped = Object.values(mapping).filter((v) => v === 'fullName' || v === 'phone')
  const canPreview = requiredMapped.length === 2 && rawRows.length > 0

  function handleFile(file: File | undefined) {
    if (!file) return
    setFileName(file.name)
    setError(null)

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      encoding: 'UTF-8',
      complete(results: ParseResult<Record<string, string>>) {
        const fields = (results.meta.fields ?? []).filter(Boolean)
        const data = results.data
        if (fields.length === 0 || data.length === 0) {
          setError('The CSV file appears empty or has no headers.')
          return
        }

        setHeaders(fields)
        setRawRows(data)
        const auto: Record<string, string> = {}
        for (const h of fields) auto[h] = autoMapHeader(h)
        setMapping(auto)
        setStep(1)
      },
      error() {
        setError('Could not read the CSV file. Check the format.')
      },
    })
  }

  function mappedRows(): Record<string, unknown>[] {
    return rawRows.map((row) => {
      const mapped: Record<string, unknown> = {}
      for (const [csvCol, field] of Object.entries(mapping)) {
        if (!field) continue
        const val = row[csvCol]
        if (val !== undefined && val !== '') {
          mapped[field] = parseMappedValue(field, val)
        }
      }
      return mapped
    })
  }

  function handlePreview() {
    setError(null)
    importMut.mutate(
      { rows: mappedRows(), duplicatePolicy, dryRun: true },
      {
        onSuccess: (data) => {
          setResult(data)
          setStep(2)
        },
        onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Preview failed'),
      },
    )
  }

  function handleExecute() {
    setError(null)
    importMut.mutate(
      { rows: mappedRows(), duplicatePolicy, dryRun: false },
      {
        onSuccess: (data) => {
          setResult(data)
          setStep(3)
        },
        onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Import failed'),
      },
    )
  }

  function handleReset() {
    setStep(0)
    setFileName('')
    setHeaders([])
    setRawRows([])
    setMapping({})
    setResult(null)
    setError(null)
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/clients"
            className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to clients
          </Link>
          <h1 className="mt-1 text-2xl font-semibold">Import Clients</h1>
        </div>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2 text-sm">
        {['Upload', 'Map Columns', 'Preview', 'Done'].map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <Badge variant={step === i ? 'default' : step > i ? 'success' : 'muted'}>{i + 1}</Badge>
            <span className={step === i ? 'font-medium' : 'text-[var(--color-muted-foreground)]'}>
              {label}
            </span>
            {i < 3 && <span className="text-[var(--color-muted-foreground)]">→</span>}
          </div>
        ))}
      </div>

      {error && (
        <Card className="border-[var(--color-destructive)] bg-[var(--color-destructive)]/5 p-4">
          <p className="flex items-center gap-2 text-sm font-medium text-[var(--color-destructive)]">
            <FileWarning className="h-4 w-4" />
            {error}
          </p>
        </Card>
      )}

      {/* Step 0: Upload */}
      {step === 0 && (
        <div
          className="flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed border-[var(--color-border)] bg-[var(--color-card)] p-12 text-center hover:border-[var(--color-primary)]"
          onClick={() => fileRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click()
          }}
          role="button"
          tabIndex={0}
        >
          <FileUp className="h-10 w-10 text-[var(--color-muted-foreground)]" />
          <div>
            <p className="font-medium">Choose a CSV file or drag it here</p>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
              The first row should contain column headers. Max 500 rows.
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <Button variant="outline">Select CSV file</Button>
        </div>
      )}

      {/* Step 1: Column mapping */}
      {step === 1 && (
        <div className="space-y-4">
          <Card className="p-4">
            <p className="mb-3 text-sm text-[var(--color-muted-foreground)]">
              Map each CSV column to a client field. Columns marked with * are required.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border)] text-left">
                    <th className="px-3 py-2 font-medium">CSV Column</th>
                    <th className="px-3 py-2 font-medium">Sample Value</th>
                    <th className="px-3 py-2 font-medium">Client Field</th>
                  </tr>
                </thead>
                <tbody>
                  {headers.map((header) => (
                    <tr
                      key={header}
                      className="border-b border-[var(--color-border)] last:border-0"
                    >
                      <td className="px-3 py-2 font-mono text-xs">{header}</td>
                      <td className="max-w-[200px] truncate px-3 py-2 text-xs text-[var(--color-muted-foreground)]">
                        {rawRows[0]?.[header] ?? '—'}
                      </td>
                      <td className="px-3 py-2">
                        <Select
                          value={mapping[header] ?? ''}
                          onChange={(e) =>
                            setMapping((prev) => ({ ...prev, [header]: e.target.value }))
                          }
                          className="w-full max-w-[280px]"
                        >
                          <option value="">— Skip column —</option>
                          {IMPORT_FIELDS.map((f) => (
                            <option key={f.value} value={f.value}>
                              {f.label}
                            </option>
                          ))}
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <label
                  htmlFor="dup-policy"
                  className="text-sm text-[var(--color-muted-foreground)]"
                >
                  When a duplicate is found:
                </label>
                <Select
                  id="dup-policy"
                  className="h-8 w-28"
                  value={duplicatePolicy}
                  onChange={(e) => setDuplicatePolicy(e.target.value as 'skip' | 'error')}
                >
                  <option value="skip">Skip</option>
                  <option value="error">Report as error</option>
                </Select>
              </div>
            </div>
          </Card>

          <div className="flex items-center justify-between">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {rawRows.length} row{rawRows.length !== 1 ? 's' : ''} detected
            </p>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={handleReset}>
                Start over
              </Button>
              <Button
                onClick={handlePreview}
                disabled={!canPreview || importMut.isPending}
                loading={importMut.isPending}
              >
                Preview import
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Preview results */}
      {step === 2 && result && (
        <div className="space-y-4">
          <Card className="p-4">
            <h2 className="text-sm font-semibold">Preview Results</h2>
            <div className="mt-3 grid grid-cols-3 gap-4">
              <Stat label="Total rows" value={result.total} />
              <Stat label="Valid" value={result.valid} tone="success" />
              <Stat
                label="Errors"
                value={result.errors.length}
                tone={result.errors.length > 0 ? 'destructive' : undefined}
              />
              <Stat
                label="Duplicates"
                value={result.duplicates.length}
                tone={result.duplicates.length > 0 ? 'warning' : undefined}
              />
            </div>

            {result.errors.length > 0 && (
              <div className="mt-4">
                <h3 className="mb-2 text-sm font-medium text-[var(--color-destructive)]">Errors</h3>
                <div className="max-h-48 overflow-y-auto rounded border border-[var(--color-border)]">
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--color-muted)]/50">
                      <tr className="text-left">
                        <th className="px-3 py-1.5 font-medium">Row</th>
                        <th className="px-3 py-1.5 font-medium">Field</th>
                        <th className="px-3 py-1.5 font-medium">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.slice(0, 50).map((e, i) => (
                        <tr key={i} className="border-t border-[var(--color-border)]">
                          <td className="px-3 py-1.5">{e.row}</td>
                          <td className="px-3 py-1.5 font-mono text-xs">{e.path}</td>
                          <td className="px-3 py-1.5 text-[var(--color-destructive)]">
                            {e.message}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.duplicates.length > 0 && (
              <div className="mt-4">
                <h3 className="mb-2 text-sm font-medium text-[var(--color-warning)]">
                  Duplicates Skipped
                </h3>
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  {result.duplicates.length} row{result.duplicates.length !== 1 ? 's' : ''} matched
                  existing clients and will be skipped.
                </p>
              </div>
            )}
          </Card>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(1)}>
              Back to mapping
            </Button>
            <Button
              onClick={handleExecute}
              loading={importMut.isPending}
              disabled={result.valid === 0}
            >
              Import {result.valid} client{result.valid !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Done */}
      {step === 3 && result && (
        <Card className="p-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-primary)]/10">
            <Check className="h-6 w-6 text-[var(--color-primary)]" />
          </div>
          <h2 className="text-lg font-semibold">Import Complete</h2>

          <div className="mt-6 grid grid-cols-3 gap-4">
            <Stat label="Total" value={result.total} />
            <Stat label="Created" value={result.created} tone="success" />
            <Stat
              label="Skipped"
              value={result.errors.length + result.duplicates.length}
              tone={result.errors.length + result.duplicates.length > 0 ? 'muted' : undefined}
            />
          </div>

          {result.errors.length > 0 && (
            <div className="mt-4 text-left">
              <h3 className="mb-2 text-sm font-medium text-[var(--color-destructive)]">
                {result.errors.length} error{result.errors.length !== 1 ? 's' : ''}
              </h3>
              <div className="max-h-32 overflow-y-auto rounded border border-[var(--color-border)]">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-muted)]/50">
                    <tr className="text-left">
                      <th className="px-3 py-1.5 font-medium">Row</th>
                      <th className="px-3 py-1.5 font-medium">Field</th>
                      <th className="px-3 py-1.5 font-medium">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.slice(0, 30).map((e, i) => (
                      <tr key={i} className="border-t border-[var(--color-border)]">
                        <td className="px-3 py-1.5">{e.row}</td>
                        <td className="px-3 py-1.5 font-mono text-xs">{e.path}</td>
                        <td className="px-3 py-1.5 text-[var(--color-destructive)]">{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-center gap-3">
            <Link to="/clients" className="inline-flex">
              <Button>View clients</Button>
            </Link>
            <Button variant="outline" onClick={handleReset}>
              Import another file
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: 'success' | 'destructive' | 'warning' | 'muted'
}) {
  const color = {
    success: 'text-[var(--color-success)]',
    destructive: 'text-[var(--color-destructive)]',
    warning: 'text-[var(--color-warning)]',
    muted: 'text-[var(--color-muted-foreground)]',
    default: '',
  }[tone ?? 'default']

  return (
    <div className="rounded-lg border border-[var(--color-border)] p-3 text-center">
      <p className={cn('text-2xl font-semibold', color)}>{value.toLocaleString()}</p>
      <p className="text-xs text-[var(--color-muted-foreground)]">{label}</p>
    </div>
  )
}
